import React from 'react';
import { observer } from 'src/utils/mobx-react';
import { CustomerButton } from 'src/components/FraudAndCompliance/CustomerButton/CustomerButton';
import ConfirmationBox from 'src/components/ConfirmationBox/ConfirmationBox.jsx';
import { useAppStateContext } from 'src/AppState/AppState';
import { OptionType, WithdrawalRequestsRowState } from './WithdrawalRequestsRowState';
import { TableRow, TableCell, TableSelectCell } from './WithdrawalRequests.style';
import { ViewType } from 'src/AppState/WithdrawalState';
import {
    ChevronDownIconWrapper,
    Select,
    SelectWrapper,
} from 'src/components/FraudAndCompliance/FraudAndCompliancesTab/FraudAndCompliances/components/CommonTable.styles';
import { isCurrencyCode } from 'src_common/common/amount/website-money/currency';
import { ModelsState } from 'src/AppState/Models/ModelsState';
import { DateTime } from 'src_common/utils/time/time';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';
import { useCommon } from 'src/AppState/Common';

const getOption = (state: OptionType): React.ReactElement => {
    return (
        <option
            key={state.value}
            value={state.value}
        >
            {state.label}
        </option>
    );
};

export interface ActionFormNewPropsType {
    type: ViewType;
    state: WithdrawalRequestsRowState;
    dataTest?: string;
}

const ActionFormNew = observer('ActionFormNew', (props: ActionFormNewPropsType): React.ReactElement => {
    const { state, dataTest } = props;

    return (
        <SelectWrapper>
            <Select
                name='action'
                onChange={state.onChange}
                disabled={state.selectIsDisabled}
                value={state.currentSelectedValue}
                data-test={dataTest}
            >
                <option>Choose an action</option>
                {state.optionList.map(getOption)}
            </Select>
            <ChevronDownIconWrapper />
        </SelectWrapper>
    );
});

interface ConfirmationPropsType {
    state: WithdrawalRequestsRowState;
}

const Confirmation = observer('Confirmation', (props: ConfirmationPropsType): React.ReactElement => {
    const { state } = props;

    const message = state.confirmationMessage;

    if (message === null) {
        return <div />;
    }

    return (
        <ConfirmationBox
            message={message}
            onConfirm={state.onConfirm}
            onReject={state.onReject}
        />
    );
});

interface WithdrawalRequestsRowPropsType {
    withdrawalId: number;
    type: ViewType;
    dataTest?: string;
    models: ModelsState;
}

const formatCardDetails = (
    cardType: string | undefined | null,
    cardNumber: string,
    expires: string | undefined | null
): string => {
    const defaultString = `${cardType} **** ${cardNumber}`;
    const expiresDate = expires ?? null;
    if (expiresDate === null) {
        return defaultString;
    }

    const formatedExpiresDate = DateTime.from(expiresDate)?.format('MM/YY');
    return `${defaultString}, ${formatedExpiresDate}`;
};

export const WithdrawalRequestsRow = observer(
    'WithdrawalRequestsRow',
    ({ type, withdrawalId, dataTest, models }: WithdrawalRequestsRowPropsType): React.ReactElement | null => {
        const appState = useAppStateContext();
        const common = useCommon();
        const configState = ConfigState.get(common);

        const [state] = React.useState(
            () =>
                new WithdrawalRequestsRowState(
                    appState.withdrawalState,
                    configState,
                    appState.permissionGrantsState,
                    () => type,
                    () => withdrawalId
                )
        );

        const withdrawal = appState.withdrawalState.getItem(type, withdrawalId);

        if (withdrawal === null) {
            return null;
        }

        const customerId = withdrawal.accountId;
        if (!isCurrencyCode(withdrawal.currency)) {
            console.error('Incorrect currency');
            return null;
        }

        const amount = appState.configState.precision.newFromAnything(withdrawal.amount).format(withdrawal.currency);
        const bankAccountName = withdrawal.bankAccountName ?? '';

        return (
            <TableRow
                section={type}
                data-test={dataTest}
            >
                <TableCell data-test='withdrawal-date'>
                    {DateTime.from(withdrawal.tdate)?.format('DD-MM-YYYY HH:mm')}
                </TableCell>
                <TableCell data-test='withdrawal-customer'>
                    <CustomerButton
                        customerId={customerId}
                        models={models}
                    />
                </TableCell>
                <TableCell data-test='withdrawal-amount'>{amount}</TableCell>
                <TableCell data-test='withdrawal-card-details'>
                    {formatCardDetails(withdrawal.cardType, withdrawal.cardNumber, withdrawal.expires)}
                </TableCell>
                <TableCell data-test='withdrawal-bank-account-holder'>{bankAccountName}</TableCell>
                <TableSelectCell>
                    <ActionFormNew
                        state={state}
                        type={type}
                        dataTest='withdrawal-action-form'
                    />
                    <Confirmation state={state} />
                </TableSelectCell>
            </TableRow>
        );
    }
);
